import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Link } from 'react-router-dom'
import { Card } from '@magnetic/card'
import { Button } from '@magnetic/button'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Spinner } from '@magnetic/spinner'
import { Tag } from '@magnetic/tag'
import { Text } from '@magnetic/text'
import {
  WarningCircle,
  ArrowsClockwise,
  ArrowRight,
  Warning,
  XCircle,
  Clock,
  Trash,
  CheckCircle
} from '@/lib/icons'
import { listApplicationsWithIssues } from '@/lib/api'
import { formatTimestamp } from '@/lib/protobuf-utils'
import { useIssueActions } from '@/hooks/useIssueActions'

export function IssuesPage() {
  const { data: issuesResponse, isLoading, error } = useConnectQuery(
    listApplicationsWithIssues,
    {},
    {
      refetchInterval: 30000,
    }
  )

  const applications = issuesResponse?.applications || []
  const issueActions = useIssueActions()

  const getIssueIcon = (issueType: string) => {
    switch (issueType) {
      case 'degraded':
        return <XCircle size={20} weight="fill" />
      case 'crd_error':
        return <Warning size={20} weight="fill" />
      case 'setup_error':
        return <Warning size={20} weight="fill" />
      case 'out_of_sync':
        return <ArrowsClockwise size={20} />
      case 'unhealthy':
        return <WarningCircle size={20} />
      case 'ecr_error':
        return <WarningCircle size={20} />
      default:
        return <Clock size={20} style={{ opacity: 0.5 }} />
    }
  }

  const getIssueBadge = (issueType: string) => {
    const styles: Record<string, { status: 'negative' | 'warning' | 'inactive'; label: string }> = {
      degraded: {
        status: 'negative',
        label: 'Degraded',
      },
      crd_error: {
        status: 'negative',
        label: 'CRD Error',
      },
      setup_error: {
        status: 'negative',
        label: 'Setup Error',
      },
      out_of_sync: {
        status: 'warning',
        label: 'Out of Sync',
      },
      unhealthy: {
        status: 'warning',
        label: 'Unhealthy',
      },
      ecr_error: {
        status: 'negative',
        label: 'ECR Error',
      },
    }
    const style = styles[issueType] || {
      status: 'inactive' as const,
      label: issueType,
    }
    return <Tag.Status status={style.status}>{style.label}</Tag.Status>
  }

  if (error) {
    return (
      <Flex direction="vertical" align="center" gap="md" style={{ padding: 48 }}>
        <WarningCircle size={48} style={{ marginBottom: 16 }} />
        <Heading size="sub-section">Failed to load issues</Heading>
        <Text size="sm" color="light">
          {error instanceof Error ? error.message : 'Unknown error'}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex direction="vertical" gap="lg">
      <Flex direction="vertical" gap="xs">
        <Heading size="primary">Issues</Heading>
        <Text size="sm" color="light">Applications that require attention</Text>
      </Flex>

      {isLoading ? (
        <Flex align="center" justify="center" style={{ padding: 48 }}>
          <Spinner size="lg" />
        </Flex>
      ) : applications.length === 0 ? (
        <Card style={{ boxShadow: 'none' }}>
          <Flex direction="vertical" align="center" gap="md" style={{ padding: 48 }}>
            <CheckCircle size={48} weight="fill" style={{ marginBottom: 16 }} />
            <Heading size="sub-section">No issues found</Heading>
            <Text size="sm" color="light">All your applications are healthy and in sync!</Text>
          </Flex>
        </Card>
      ) : (
        <Card style={{ boxShadow: 'none' }}>
          <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
            <Flex align="center" gap="sm">
              <WarningCircle size={20} />
              <Heading size="sub-section">
                {applications.length} {applications.length === 1 ? 'Issue' : 'Issues'} Found
              </Heading>
            </Flex>

            <Flex direction="vertical" gap="sm">
      {applications.map((item) => {
                const app = item.application
                if (!app) return null
                const isSyncing = issueActions.isSyncing(app.id)
                const isRetryingCRD = issueActions.isRetryingCRD(app.id)
                const isRetryingSetup = issueActions.isRetryingSetup(app.id)
                const isDeleting = issueActions.isDeleting(app.id)

                return (
                  <Flex
                    key={app.id}
                    align="center"
                    justify="space-between"
                    style={{
                      padding: 16,
                      borderRadius: 8,
                      border: '1px solid var(--mds-color-theme-outline-secondary-normal)',
                    }}
                  >
                    <Flex align="center" gap="md">
                      {getIssueIcon(item.issueType)}
                      <Flex direction="vertical" gap="xs">
                        <Flex align="center" gap="sm">
                          <Text size="sm" weight="semi-bold">{app.displayName || app.name}</Text>
                          {getIssueBadge(item.issueType)}
                        </Flex>
                        <Flex gap="sm" style={{ opacity: 0.7 }}>
                          <Text size="sm" weight="semi-bold">{item.projectName}</Text>
                          <Text size="sm">•</Text>
                          <Text size="sm">{item.issueDescription}</Text>
                        </Flex>
                        {app.lastSyncTime && (
                          <Text size="xs" color="light">
                            Last sync: {formatTimestamp(app.lastSyncTime)}
                          </Text>
                        )}
                      </Flex>
                    </Flex>

                    <Flex align="center" gap="sm">
                      {item.issueType === 'out_of_sync' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            issueActions.sync(app.id)
                          }}
                          disabled={isSyncing}
                          icon={isSyncing
                            ? <Spinner size="xs" />
                            : <ArrowsClockwise size={14} />
                          }
                        >
                          {isSyncing ? 'Syncing...' : 'Sync'}
                        </Button>
                      )}

                      {item.issueType === 'crd_error' && (
                        <>
                          <Button
                            kind="secondary"
                            size="sm"
                            onClick={() => {
                              issueActions.retryCRD(app.id)
                            }}
                            disabled={isRetryingCRD}
                            icon={isRetryingCRD
                              ? <Spinner size="xs" />
                              : <ArrowsClockwise size={14} />
                            }
                          >
                            {isRetryingCRD ? 'Retrying...' : 'Retry'}
                          </Button>
                          <Button
                            destructive
                            size="sm"
                            onClick={() => {
                              if (confirm('Delete this failed application?')) {
                                issueActions.forceDelete(app.id)
                              }
                            }}
                            disabled={isDeleting}
                            icon={isDeleting
                              ? <Spinner size="xs" />
                              : <Trash size={14} />
                            }
                          >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                          </Button>
                        </>
                      )}

                      {item.issueType === 'setup_error' && (
                        <Button
                          kind="secondary"
                          size="sm"
                          onClick={() => {
                            issueActions.retrySetup(app.id)
                          }}
                          disabled={isRetryingSetup}
                          icon={isRetryingSetup
                            ? <Spinner size="xs" />
                            : <ArrowsClockwise size={14} />
                          }
                        >
                          {isRetryingSetup ? 'Retrying...' : 'Retry Setup'}
                        </Button>
                      )}

                      <Link to={`/projects/${app.projectId}/applications/${app.id}`}>
                        <Button kind="tertiary" size="sm" icon={<ArrowRight size={16} />} />
                      </Link>
                    </Flex>
                  </Flex>
                )
              })}
            </Flex>
          </Flex>
        </Card>
      )}
    </Flex>
  )
}
